import { API_SITE, SESSION_KEY, apiBase, accessToken, DEFAULT_LIMIT } from '../turnover/helpers.js';
import { formatCommissionMoney } from '../affiliate/helpers.js';
import { dedupeAccountFetch } from '../boot/accountCatalogCache.js';

export { API_SITE, SESSION_KEY, apiBase, accessToken, DEFAULT_LIMIT, formatCommissionMoney };

export function referralOrigin() {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return '';
}

export function buildReferralLink(code, origin = referralOrigin()) {
  const normalized = String(code || '').trim().toUpperCase();
  if (!normalized) return '';
  const base = String(origin || '').replace(/\/$/, '');
  return `${base}/signup?ref=${encodeURIComponent(normalized)}`;
}

export function formatSiteDateTime(value, lang = 'en') {
  if (!value) return '—';
  try {
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleString(lang === 'bn' ? 'bn-BD' : 'en-GB', {
      timeZone: 'Asia/Dhaka',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export function formatMoney(value, lang = 'en') {
  const n = Number(value);
  if (!Number.isFinite(n)) return '৳0';
  const loc = lang === 'bn' ? 'bn-BD' : 'en-US';
  const abs = Math.abs(n).toLocaleString(loc, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return `${n < 0 ? '-' : ''}৳${abs}`;
}

export async function copyText(value) {
  const text = String(value || '');
  if (!text) return false;
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallback */
  }
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    return true;
  } catch {
    return false;
  }
}

export async function shareReferralLink({ title, text, url }) {
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text, url });
      return { ok: true, method: 'share' };
    } catch (err) {
      if (err && err.name === 'AbortError') return { ok: false, reason: 'cancelled' };
    }
  }
  const copied = await copyText(url);
  return copied ? { ok: true, method: 'copy' } : { ok: false, reason: 'failed' };
}

export async function fetchReferralStats(opts = {}) {
  return dedupeAccountFetch('GET:/referrals', async () => {
    const token = accessToken();
    const res = await fetch(`${apiBase()}/referrals`, {
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      cache: 'no-store',
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(json.message || json.error || `Request failed (${res.status})`);
      err.status = res.status;
      throw err;
    }
    return json.data || json;
  }).then((data) => {
    if (opts.signal && opts.signal.aborted) {
      const err = new Error('AbortError');
      err.name = 'AbortError';
      throw err;
    }
    return data;
  });
}

export async function fetchReferredCustomers({ page = 1, limit = DEFAULT_LIMIT } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const token = accessToken();
  const res = await fetch(`${apiBase()}/referrals/customers?${params}`, {
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    cache: 'no-store',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(json.message || json.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return {
    items: json.items || json.data || [],
    page: json.page || page,
    limit: json.limit || limit,
    total: json.total || 0,
    totalPages: json.totalPages || 0,
    customerType: json.customerType || 'general',
  };
}
